import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { Copy, EnvelopeSimple, FileArrowUp, FileText, GlobeHemisphereWest, ImageSquare, LinkSimple, LockSimple, UserCircle, ChatCircleText, PaperPlaneTilt, CircleNotch } from "@phosphor-icons/react";

function Toggle({ checked, onChange, testId, label }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm font-medium" data-testid={testId}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function ClientPortalManager({ businessId }) {
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [activity, setActivity] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    try {
      const [s, c, a] = await Promise.all([
        api.get(`/businesses/${businessId}/client-portal/settings`),
        api.get(`/businesses/${businessId}/client-portal/clients`),
        api.get(`/businesses/${businessId}/client-portal/activity`),
      ]);
      setSettings(s.data);
      setClients(c.data.clients || []);
      setActivity(a.data.activity || []);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const copyUrl = async () => {
    await navigator.clipboard.writeText(settings?.portal_url || "");
    toast.success("Portal URL copied");
  };

  const saveSettings = async (patch = {}) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/businesses/${businessId}/client-portal/settings`, { ...settings, ...patch });
      setSettings(data);
      toast.success("Portal settings saved");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    const fd = new FormData();
    fd.append("file", logoFile);
    setBusy("logo");
    try {
      const { data } = await api.post(`/businesses/${businessId}/client-portal/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, logo_url: data.logo_url }));
      toast.success("Portal logo updated");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusy("");
    }
  };

  const updateClient = async (clientId, patch) => {
    setBusy(clientId);
    try {
      const { data } = await api.patch(`/businesses/${businessId}/client-portal/clients/${clientId}`, patch);
      setClients((xs) => xs.map((x) => (x.id === clientId ? { ...x, ...data } : x)));
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusy("");
    }
  };

  const inviteClient = async (client) => {
    setBusy(`invite-${client.id}`);
    try {
      await api.post(`/businesses/${businessId}/client-portal/clients/${client.id}/invite`, { email: client.email, name: client.name });
      toast.success(`Invite sent to ${client.email}`);
      load();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusy("");
    }
  };

  const uploadDoc = async (clientId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", file.name);
    setBusy(`doc-${clientId}`);
    try {
      await api.post(`/businesses/${businessId}/client-portal/clients/${clientId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document shared");
      load();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusy("");
    }
  };

  const setDraft = (clientId, patch) => {
    setDrafts((all) => ({
      ...all,
      [clientId]: {
        message_type: "general",
        subject: "",
        body: "",
        invoice_id: "",
        ...(all[clientId] || {}),
        ...patch,
      },
    }));
  };

  const sendMessage = async (client) => {
    const draft = drafts[client.id] || { message_type: "general", subject: "", body: "", invoice_id: "" };
    if (!draft.body?.trim()) return toast.error("Write a message first.");
    setBusy(`message-${client.id}`);
    try {
      await api.post(`/businesses/${businessId}/client-portal/clients/${client.id}/messages`, {
        message_type: draft.message_type,
        subject: draft.subject,
        body: draft.body,
        invoice_id: draft.invoice_id || null,
      });
      setDraft(client.id, { subject: "", body: "", invoice_id: "" });
      toast.success(`Message sent to ${client.name || client.email}`);
      load();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusy("");
    }
  };

  const sortedClients = useMemo(() => [...clients].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)), [clients]);

  return (
    <div className="space-y-6" data-testid="client-portal-manager">
      <div className="bk-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Portal settings</p>
            <h3 className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>White-label your client portal</h3>
            <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>Your clients get a private login with your branding, your invoices, and your documents — no IronRoot branding.</p>
          </div>
          <button onClick={copyUrl} data-testid="client-portal-copy-url" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}><Copy size={16} weight="bold" /> Copy portal URL</button>
        </div>
        {settings && (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6 mt-6">
            <div className="space-y-4">
              <Toggle checked={!!settings.enabled} onChange={(enabled) => saveSettings({ enabled })} testId="client-portal-enabled-toggle" label="Enable client portal" />
              <label className="block text-sm font-medium" style={{ color: "var(--bk-text)" }}>
                Welcome message
                <textarea value={settings.welcome_message || ""} onChange={(e) => setSettings((s) => ({ ...s, welcome_message: e.target.value }))} data-testid="client-portal-welcome-input" rows={3} className="w-full mt-2 rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-sm font-medium" style={{ color: "var(--bk-text)" }}>
                  Contact email
                  <input value={settings.contact_email || ""} onChange={(e) => setSettings((s) => ({ ...s, contact_email: e.target.value }))} data-testid="client-portal-contact-email" className="w-full mt-2 rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                </label>
                <label className="block text-sm font-medium" style={{ color: "var(--bk-text)" }}>
                  Contact phone
                  <input value={settings.contact_phone || ""} onChange={(e) => setSettings((s) => ({ ...s, contact_phone: e.target.value }))} data-testid="client-portal-contact-phone" className="w-full mt-2 rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                </label>
              </div>
              <div className="rounded-2xl p-4" style={{ background: "var(--bk-page)" }}>
                <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-accent)" }}>Portal URL</p>
                <p className="mt-2 text-sm font-semibold break-all" data-testid="client-portal-url" style={{ color: "var(--bk-text)" }}>{settings.portal_url}</p>
              </div>
              <button onClick={() => saveSettings()} disabled={saving} data-testid="client-portal-save-settings" className="bk-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save portal settings"}</button>
            </div>
            <div className="rounded-[24px] border p-5" style={{ borderColor: "var(--bk-border)", background: "linear-gradient(135deg, rgba(255,249,240,0.7), #fff)" }}>
              <div className="aspect-[4/3] rounded-[20px] border overflow-hidden flex items-center justify-center" style={{ borderColor: "var(--bk-border)", background: "#fff" }}>
                {settings.logo_url ? <img src={settings.logo_url} alt="Portal logo" className="max-h-28 max-w-[80%] object-contain" data-testid="client-portal-logo-preview" /> : <ImageSquare size={42} weight="duotone" color="var(--bk-accent)" />}
              </div>
              <label className="block mt-4 text-sm font-medium" style={{ color: "var(--bk-text)" }}>
                Portal logo
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="client-portal-logo-input" className="w-full mt-2 rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
              </label>
              <button onClick={uploadLogo} disabled={!logoFile || busy === "logo"} data-testid="client-portal-logo-upload" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold disabled:opacity-60" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)" }}><FileArrowUp size={16} weight="bold" /> {busy === "logo" ? "Uploading…" : "Upload logo"}</button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6">
        <div className="bk-card p-6" data-testid="client-portal-clients-card">
          <div className="flex items-center gap-2 mb-4">
            <UserCircle size={20} weight="duotone" color="var(--bk-accent)" />
            <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Clients & visibility controls</h4>
          </div>
          <div className="space-y-4">
            {sortedClients.map((client) => (
              <div key={client.id} className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)" }} data-testid={`portal-client-${client.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{client.name || client.email}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{client.email}</p>
                    {client.last_invoice_viewed_at && <p className="text-xs mt-1" style={{ color: "var(--bk-success)" }}>Last invoice viewed: {new Date(client.last_invoice_viewed_at).toLocaleString()}</p>}
                  </div>
                  <button onClick={() => inviteClient(client)} disabled={busy === `invite-${client.id}`} data-testid={`portal-invite-${client.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border disabled:opacity-60" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)" }}><EnvelopeSimple size={14} weight="bold" /> {busy === `invite-${client.id}` ? "Sending…" : "Invite to portal"}</button>
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  {[
                    ["invoices", "Invoices"],
                    ["payments", "Payments"],
                    ["appointments", "Appointments"],
                    ["documents", "Documents"],
                  ].map(([key, label]) => <Toggle key={key} checked={client.visibility?.[key] !== false} onChange={(checked) => updateClient(client.id, { [key]: checked })} testId={`portal-visibility-${key}-${client.id}`} label={label} />)}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold cursor-pointer" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    <FileArrowUp size={14} weight="bold" /> Share document
                    <input type="file" className="hidden" onChange={(e) => uploadDoc(client.id, e.target.files?.[0])} data-testid={`portal-upload-doc-${client.id}`} />
                  </label>
                  <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>{client.documents?.length || 0} document{(client.documents?.length || 0) === 1 ? "" : "s"} shared</span>
                </div>
                {(client.documents?.length || 0) > 0 && (
                  <div className="mt-4 rounded-2xl p-3" style={{ background: "var(--bk-page)" }}>
                    {(client.documents || []).slice(0, 4).map((doc) => <div key={doc.id} className="flex items-center justify-between gap-3 py-2 text-sm"><span style={{ color: "var(--bk-text)" }}>{doc.label || doc.filename}</span><span style={{ color: "var(--bk-text-2)" }}>{new Date(doc.created_at).toLocaleDateString()}</span></div>)}
                  </div>
                )}
                {(client.invoice_receipts?.length || 0) > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-accent)" }}>Invoice read receipts</p>
                    {(client.invoice_receipts || []).slice(0, 4).map((inv) => (
                      <div key={inv.invoice_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`portal-invoice-receipt-${inv.invoice_id}`}>
                        <span style={{ color: "var(--bk-text)" }}>Invoice #{inv.number}</span>
                        <span style={{ color: "var(--bk-text-2)" }}>{inv.client_last_viewed_at ? new Date(inv.client_last_viewed_at).toLocaleString() : "Not viewed yet"}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-5 rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid={`portal-messages-${client.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <ChatCircleText size={18} weight="duotone" color="var(--bk-accent)" />
                      <p className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Messages</p>
                    </div>
                    {client.message_unread_count > 0 && <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid={`portal-message-unread-${client.id}`}>{client.message_unread_count} unread</span>}
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {(client.messages || []).length === 0 ? (
                      <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No messages yet. Send the first one here.</p>
                    ) : (
                      (client.messages || []).map((msg) => (
                        <div key={msg.id} className="rounded-2xl p-3" style={{ background: msg.sender_type === "owner" ? "var(--bk-rowan-bg)" : "var(--bk-page)" }} data-testid={`portal-message-${client.id}-${msg.id}`}>
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-xs font-semibold" style={{ color: "var(--bk-text)" }}>{msg.sender_type === "owner" ? "You" : (client.name || client.email)} · {msg.message_type.replaceAll("_", " ")}</p>
                            <span className="text-[11px]" style={{ color: "var(--bk-text-2)" }}>{new Date(msg.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-sm font-semibold mt-2" style={{ color: "var(--bk-text)" }}>{msg.subject}</p>
                          {msg.invoice_number && <p className="text-xs mt-1" style={{ color: "var(--bk-accent)" }}>Invoice #{msg.invoice_number}</p>}
                          <p className="text-sm mt-2 whitespace-pre-line" style={{ color: "var(--bk-text-2)" }}>{msg.body}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <select value={drafts[client.id]?.message_type || "general"} onChange={(e) => setDraft(client.id, { message_type: e.target.value })} data-testid={`portal-message-type-${client.id}`} className="rounded-xl border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
                      <option value="general">General message</option>
                      <option value="invoice_question">Invoice question</option>
                      <option value="document_request">Document request</option>
                      <option value="payment_confirmation">Payment confirmation</option>
                    </select>
                    <select value={drafts[client.id]?.invoice_id || ""} onChange={(e) => setDraft(client.id, { invoice_id: e.target.value })} data-testid={`portal-message-invoice-${client.id}`} className="rounded-xl border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
                      <option value="">No linked invoice</option>
                      {(client.invoice_options || []).map((inv) => <option key={inv.id} value={inv.id}>Invoice #{inv.number}</option>)}
                    </select>
                    <input value={drafts[client.id]?.subject || ""} onChange={(e) => setDraft(client.id, { subject: e.target.value })} data-testid={`portal-message-subject-${client.id}`} placeholder="Subject" className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                  </div>
                  <textarea value={drafts[client.id]?.body || ""} onChange={(e) => setDraft(client.id, { body: e.target.value })} rows={4} data-testid={`portal-message-body-${client.id}`} placeholder="Write a secure message to your client…" className="w-full mt-3 rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                  <div className="flex justify-end mt-3">
                    <button onClick={() => sendMessage(client)} disabled={busy === `message-${client.id}`} data-testid={`portal-message-send-${client.id}`} className="bk-btn-primary px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60">
                      {busy === `message-${client.id}` ? <span className="inline-flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Sending…</span> : <span className="inline-flex items-center gap-2"><PaperPlaneTilt size={16} weight="fill" /> Send message</span>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bk-card p-6" data-testid="client-portal-activity-card">
          <div className="flex items-center gap-2 mb-4">
            <GlobeHemisphereWest size={20} weight="duotone" color="var(--bk-accent)" />
            <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Portal activity</h4>
          </div>
          <div className="space-y-3 max-h-[700px] overflow-y-auto">
            {activity.map((item) => (
              <div key={item.id} className="rounded-2xl p-4" style={{ background: "var(--bk-page)" }} data-testid={`portal-activity-${item.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{item.detail}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{item.event_type.replaceAll("_", " ")}</p>
                  </div>
                  <span className="text-xs shrink-0" style={{ color: "var(--bk-text-2)" }}>{new Date(item.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}